import React, { useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  TextField,
  Typography,
  Button,
  Grid,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
} from "@mui/material";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import E3Background from "../assets/TheE3Group.jpg";
import authStore from "../stores/AuthStore";

const SupportPage = observer(() => {
  const [inquiryType, setInquiryType] = useState("Device");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    country: "",
    phone: "",
    email: "",
  });

  useEffect(() => {
    const fullName = authStore.user?.name || "";
    const nameParts = fullName.trim().split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

    setFormData({
      firstName,
      lastName,
      country: authStore.user?.country || "",
      phone: authStore.user?.phone || "",
      email: authStore.user?.email || "",
    });
  }, [authStore.user]);

  const handleInquiryChange = (event, newType) => {
    if (newType !== null) {
      setInquiryType(newType);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Card
        elevation={3}
        sx={{
          mb: 4,
          p: 2,
          background: "#F3F4F6",
          borderLeft: "4px solid #22C55E",
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="center" mb={1}>
            <SupportAgentIcon
              fontSize="large"
              sx={{ mr: 2, color: "#22C55E" }}
            />
            <Typography variant="h4" component="h1" fontWeight="bold">
              Support Page
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Send us an email if you have any inquiries!
          </Typography>
        </CardContent>
      </Card>

      <Box
        sx={{
          position: "relative",
          maxHeight: "70vh",
          width: "100%",
          overflow: "hidden",
          borderRadius: "8px",
        }}
      >
        {/* Background Image Layer */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `url(${E3Background})`,
            backgroundSize: "contain",
            backgroundPosition: "center",
            filter: "blur(6px)",
            zIndex: 0,
          }}
        />

        {/* Foreground Content with maxWidth */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            minHeight: "100vh",
            backgroundColor: "rgba(255, 255, 255, 0.75)",
            px: 4,
            py: 8,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
            maxWidth: "1400px",
            margin: "0 auto",
          }}
        >
          {/* Left: Hero + Footer stacked vertically */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            {/* Hero Text */}
            <Box>
              <Typography variant="h4" fontWeight="bold">
                You Have Questions,
              </Typography>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                We Have Answers
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  maxWidth: 300,
                  mt: 1,
                }}
              >
                The E3 Support Page helps internal team members resolve
                technical issues, configure systems, and stay operational — all
                with a focus on Efficiency, Execution, and Empowerment.
              </Typography>
            </Box>

            {/* Footer Info in Column */}
            <Box sx={{ mt: 24 }}>
              {" "}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography fontWeight="bold">Location</Typography>
                  <Typography>183 Old Post Oak Rd</Typography>
                  <Typography>Kilgore, TX 75662</Typography>
                  <Typography>Monday–Sunday</Typography>
                  <Typography>08:00 – 22:00 (local time)</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography fontWeight="bold">Social Media</Typography>
                  <Typography>Instagram</Typography>
                  <Typography>LinkedIn</Typography>
                  <Typography>Facebook</Typography>
                  <Typography>Tiktok</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography fontWeight="bold">Email</Typography>
                  <Typography>e3support@team.app</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography fontWeight="bold">Contact</Typography>
                  <Typography>+(877) 833-26216</Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>

          {/* Right: Contact Form */}
          <Box sx={{ flex: 1, mt: -1 }}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                minHeight: 600,
                maxWidth: 500,
                width: "100%",
                borderRadius: 4,
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                marginLeft: "auto",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Tell Us What You Need
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="First Name"
                    fullWidth
                    size="small"
                    value={formData.firstName}
                    onChange={(e) =>
                      setFormData({ ...formData, firstName: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Last Name"
                    fullWidth
                    size="small"
                    value={formData.lastName}
                    onChange={(e) =>
                      setFormData({ ...formData, lastName: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={6} sx={{ mt: 1.5 }}>
                  <TextField
                    label="Country"
                    fullWidth
                    size="small"
                    value={formData.country}
                    onChange={(e) =>
                      setFormData({ ...formData, country: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={6} sx={{ mt: 1.5 }}>
                  <TextField
                    label="Phone Number"
                    fullWidth
                    size="small"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sx={{ mt: 1.5 }}>
                  <TextField
                    label="Email Address"
                    fullWidth
                    size="small"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" mb={1}>
                    Type of Inquiry
                  </Typography>
                  <ToggleButtonGroup
                    fullWidth
                    color="primary"
                    value={inquiryType}
                    exclusive
                    onChange={handleInquiryChange}
                  >
                    {["Account", "Alert", "Device", "Permission", "Others"].map(
                      (label) => (
                        <ToggleButton
                          key={label}
                          value={label}
                          sx={{ textTransform: "none" }}
                        >
                          {label}
                        </ToggleButton>
                      )
                    )}
                  </ToggleButtonGroup>
                </Grid>
                <Grid item xs={12} sx={{ mt: 1.5 }}>
                  <TextField
                    label="Message"
                    fullWidth
                    multiline
                    rows={4}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{
                      borderRadius: "999px",
                      backgroundColor: "#22C55E",
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "#16A34A",
                      },
                    }}
                  >
                    Submit
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

export default SupportPage;
